import React, { useEffect, useState } from 'react';
import WelcomeScreen from './WelcomeScreen';
import ReportSelectionModal from './ReportSelectionModal';
import EditReportModal from './EditReportModal';
import TasksView from './TasksView';
import ReportStatusModal from './ReportStatusModal';
import { type CnReport, type ReportFormData } from './types';
import {
  createCnReport,
  type PartnerLoginData,
  type PartnerReportStatus,
  reportBuilderUrl,
  selectCnReport,
  updateCnReport,
} from './utils';

type View = 'tasks' | 'published' | 'completed' | 'welcome' | 'reports';

// `in_progress` redirects instead; anything unknown renders nothing.
const INITIAL_VIEW: Partial<Record<PartnerReportStatus, View>> = {
  has_tasks: 'tasks',
  published: 'published',
  completed: 'completed',
  must_create: 'welcome',
  select_or_create: 'welcome',
  has_reports: 'reports',
};

const goToReportBuilder = (reportId: number) => {
  window.location.href = reportBuilderUrl(reportId);
};

const toErrorMessage = (err: unknown) =>
  err instanceof Error ? err.message : 'Unknown error';

/** Original Charity Navigator flow: pick / create a report, then open the report builder. */
export default function ReportFlow({ loginData }: { loginData: PartnerLoginData }) {
  const tiToken = loginData.ti_token;
  const [view, setView] = useState<View | undefined>(
    () => INITIAL_VIEW[loginData.report_status ?? ''],
  );
  const [availableReports, setAvailableReports] = useState<CnReport[]>(
    () => loginData.reports ?? [],
  );
  const [editingReport, setEditingReport] = useState<CnReport | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Report exists and is in progress - go straight to the wizard
    if (loginData.report_status === 'in_progress' && loginData.report_id) {
      goToReportBuilder(loginData.report_id);
      return;
    }

    // A CN account only has one report - send the user straight to it.
    if (loginData.report_status === 'has_reports') {
      const reportId = loginData.report_id ?? loginData.reports?.[0]?.id;
      if (reportId) goToReportBuilder(reportId);
    }
  }, [loginData]);

  const handleReportSelection = async (reportId: number) => {
    try {
      await selectCnReport(tiToken, reportId);
      goToReportBuilder(reportId);
    } catch (err) {
      setError(`Failed to select report: ${toErrorMessage(err)}`);
    }
  };

  const handleCreateReport = async (formData: ReportFormData) => {
    try {
      const { report } = await createCnReport(tiToken, formData);
      goToReportBuilder(report.id);
    } catch (err) {
      setError(`Failed to create report: ${toErrorMessage(err)}`);
    }
  };

  const handleSaveEdit = async (formData: ReportFormData) => {
    if (!editingReport) return;
    try {
      const { report } = await updateCnReport(tiToken, editingReport.id, formData);
      setAvailableReports(prev =>
        prev.map(r => (r.id === editingReport.id ? report : r)),
      );
      setEditingReport(null);
    } catch (err) {
      setError(`Failed to update report: ${toErrorMessage(err)}`);
    }
  };

  return (
    <>
      {view === 'published' && (
        <ReportStatusModal
          partner="cn"
          title="Report Published"
          description="Your report has been published."
          buttonLabel="View Published Report"
          reportId={loginData.report_id ?? null}
        />
      )}

      {view === 'completed' && (
        <ReportStatusModal
          partner="cn"
          title="Report Already Shared"
          description="Thank you for sharing your Impact Report with Charity Navigator."
          buttonLabel="View Report"
          reportId={loginData.report_id ?? null}
        />
      )}

      {view === 'tasks' && (
        <TasksView tasks={loginData.tasks ?? []} onViewReport={goToReportBuilder} />
      )}

      {view === 'welcome' && (
        <WelcomeScreen onContinue={() => setView('reports')} />
      )}

      {view === 'reports' && (
        <ReportSelectionModal
          reports={availableReports}
          onSelectReport={handleReportSelection}
          onEditReport={setEditingReport}
          onCreateReport={handleCreateReport}
        />
      )}

      {editingReport && (
        <EditReportModal
          report={editingReport}
          onSave={handleSaveEdit}
          onCancel={() => setEditingReport(null)}
        />
      )}

      {error && <p className="cn-login-error">{error}</p>}
    </>
  );
}
